import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import {
  X,
  Paperclip,
  Download,
  Eye,
  Image,
  FileText,
  Table2,
  File,
  FolderOpen,
  Loader2
} from 'lucide-react';
import fileService from '../services/fileService';
import FilePreviewComponent from './FilePreviewComponent';

const ProductAttachmentPreviewDialog = ({ open, onClose, product, productMongoId }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewFile, setPreviewFile] = useState(null);

  // Load attachments when dialog opens
  const loadAttachments = useCallback(async () => {
    if (!productMongoId) return;

    try {
      setLoading(true);
      setError('');

      const data = await fileService.getProductAttachments(productMongoId);
      setAttachments(data.attachments || []);
    } catch (err) {
      console.error('Failed to load attachments:', err);
      setError('加载附件失败');
    } finally {
      setLoading(false);
    }
  }, [productMongoId]);

  useEffect(() => {
    if (open && productMongoId) {
      loadAttachments();
    } else {
      setAttachments([]);
      setError('');
    }
  }, [open, productMongoId, loadAttachments]);

  // Download file
  const handleDownload = async (file) => {
    try {
      await fileService.downloadFile(file.file_id, file.filename);
    } catch (error) {
      console.error('Download error:', error);
      setError('下载文件失败');
    }
  };

  // Get file icon based on category
  const getFileIcon = (filename, category) => {
    const iconClass = "h-6 w-6";

    switch (category || fileService.getFileCategory(filename)) {
      case 'image':
        return <Image className={`${iconClass} text-primary`} />;
      case 'document':
        return <FileText className={`${iconClass} text-destructive`} />;
      case 'spreadsheet':
        return <Table2 className={`${iconClass} text-success`} />;
      default:
        return <File className={iconClass} />;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl h-[70vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip className="h-5 w-5 text-primary" />
                <div className="min-w-0">
                  <DialogTitle>产品附件</DialogTitle>
                  {product && (
                    <p className="text-sm text-muted-foreground truncate">
                      {product.product_id || product.product_code} - {product.name}
                    </p>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-1">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : attachments.length === 0 ? (
              <div className="text-center py-16">
                <FolderOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">
                  暂无附件
                </h3>
                <p className="text-sm text-muted-foreground">
                  该产品还没有上传任何附件
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  共 {attachments.length} 个文件
                </p>

                <div className="space-y-1">
                  {attachments.map((file, index) => (
                    <React.Fragment key={file.file_id}>
                      <div
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-muted ${
                          file.canPreview ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => file.canPreview && setPreviewFile(file)}
                      >
                        <div className="flex-shrink-0">
                          {getFileIcon(file.filename, file.category)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate flex-1">
                              {file.filename}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {file.displaySize}
                            </span>
                          </div>
                          <div className="mt-1">
                            {file.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {file.description}
                              </p>
                            )}
                            {file.tags && file.tags.length > 0 && (
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {file.tags.slice(0, 3).map((tag, tagIndex) => (
                                  <Badge key={tagIndex} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                                {file.tags.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{file.tags.length - 3}
                                  </Badge>
                                )}
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(file.created_at).toLocaleDateString()} • {file.categoryDisplay}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-1 flex-shrink-0">
                          {file.canPreview && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewFile(file);
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>预览</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(file);
                                  }}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>下载</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      {index < attachments.length - 1 && <Separator />}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>关闭</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      {previewFile && (
        <FilePreviewComponent
          file={previewFile}
          open={Boolean(previewFile)}
          onClose={() => setPreviewFile(null)}
          onDownload={() => handleDownload(previewFile)}
          showActions={true}
        />
      )}
    </>
  );
};

export default ProductAttachmentPreviewDialog;
